package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.feature.admin.domain.entity.AdminClaudeUsage
import com.milta.core.localization.LocalStrings
import com.milta.feature.aicredit.presentation.AiCreditFormat
import com.milta.feature.admin.domain.entity.AdminClaudeUsageAccount
import com.milta.feature.admin.domain.entity.AdminClaudeUsageEvent
import com.milta.feature.admin.domain.entity.AdminClaudeUsageOrganisation
import com.milta.feature.admin.domain.entity.AdminClaudeUsagePeriod
import com.milta.feature.admin.domain.usecase.GetAdminClaudeUsageEventsUseCase
import com.milta.feature.admin.domain.usecase.GetAdminClaudeUsageUseCase
import com.milta.feature.admin.presentation.AdminClaudeUsageIntent
import com.milta.feature.admin.presentation.AdminClaudeUsageState
import com.milta.feature.admin.presentation.AdminClaudeUsageViewModel
import com.milta.feature.admin.presentation.AdminClaudeUsageViewMode
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject

// Exactly 6 columns, matching the 6 cells every row (header/account/org) actually renders below —
// the previous template had a leftover 6th "24px" column (meant for a chevron that was never added
// as content), so the Échec chip (the real 6th child) was squeezed into that 24px slot and
// overflowed the table. Fixed widths now reflect each cell's real content.
private const val ClaudeUsageGridTemplate = "2fr 0.9fr 0.9fr 1.3fr 1.5fr 0.9fr"

/** Eonix-internal screen labels — literal French, same convention as [AdminDestination] itself
 * (never shown to a Milta client, never piped through LocalStrings). */
private object S {
    const val title = "Consommation IA — par utilisateur"
    const val subtitle = "Volume de tokens Claude consommés par chaque compte, en production (scans, imports). Lecture seule."
    const val periodToday = "Aujourd'hui"
    const val period7d = "7 jours"
    const val period30d = "30 jours"
    const val periodAll = "Tout"
    const val viewAccount = "Par compte"
    const val viewOrganisation = "Par organisation"
    const val kpiTokens = "Tokens consommés"
    const val kpiCalls = "Appels IA"
    const val kpiFailureRate = "Taux d'échec"
    const val kpiAccounts = "Comptes actifs"
    const val kpiCost = "Coût estimé"
    const val kpiCostSubtitle = "estimation ~ , pas une facture réelle"
    const val detailCostTotal = "Coût estimé (total)"
    const val detailCostAverage = "moyenne / appel"
    const val detailCostNote = "estimation à partir du tarif public Anthropic — pas une facture réelle"
    const val retentionBanner = "Fichier source (photo/PDF/Excel envoyé) conservé dans MinIO 8 jours maximum, puis purgé automatiquement. Au-delà, le détail d'un appel reste consultable mais le fichier affiche « Expiré »."
    const val columnUser = "Utilisateur"
    const val columnTokensIn = "Tokens in"
    const val columnTokensOut = "Tokens out"
    const val columnPercent = "% du total"
    const val columnBreakdown = "Répartition"
    const val columnFailure = "Échec"
    const val emptyState = "Aucune consommation sur cette période."
    const val loading = "Chargement…"
    const val errorOffline = "Serveur injoignable — vérifiez votre connexion."
    const val errorGeneric = "Une erreur est survenue. Réessayez plus tard."
    const val detailDate = "Date"
    const val detailAction = "Type d'action"
    const val detailTokensIn = "Tokens in"
    const val detailTokensOut = "Tokens out"
    const val detailSource = "Entrée"
    const val detailOutput = "Sortie Claude"
    const val detailResult = "Résultat"
    const val detailCost = "Coût ~"
    const val fileAvailable = "📎 Voir"
    const val fileExpired = "🗑 Expiré"
    const val outputAvailable = "👁 Voir"
    const val outputUnavailable = "👁 —"
    const val outcomeSuccess = "Succès"
    const val outcomeFailed = "Échec"
    const val outcomeUnreadable = "Illisible"
}

private fun sourceEndpointLabel(value: String) = when (value) {
    "ScanDeliveryList" -> "Scan liste papier"
    "ScanPackageLabel" -> "Scan étiquette"
    "ImportPdf" -> "Import PDF"
    "ImportUnstructured" -> "Import non structuré"
    "SpreadsheetMapping" -> "Mapping Excel"
    else -> value
}

private fun outcomeLabel(value: String) = when (value) {
    "Success" -> S.outcomeSuccess
    "Failed" -> S.outcomeFailed
    "Unreadable" -> S.outcomeUnreadable
    else -> value
}

// Reuses the existing design-system palette (WebImportRouteScreen.kt) rather than inventing new
// hex values — 3 blues already used for primary emphasis, teal/gold already used elsewhere for
// semantic accents.
private val BreakdownColors = listOf(WebPrimary, WebPrimaryMid, WebPrimaryLight, WebTealAccent, WebWarning)
private val BreakdownOrder = listOf("ScanDeliveryList", "ScanPackageLabel", "ImportPdf", "ImportUnstructured", "SpreadsheetMapping")

/**
 * Admin "Conso. IA" screen (2026-10-05) — real, per-account/per-organisation production Claude
 * token consumption (GetAdminClaudeUsageQuery), distinct from AdminOcrAnalysisContent's background
 * cost-comparison experiment: every row here is a real, billed call.
 */
@Composable
fun AdminClaudeUsageContent() {
    val getUsage: GetAdminClaudeUsageUseCase = koinInject()
    val getEvents: GetAdminClaudeUsageEventsUseCase = koinInject()
    val viewModel = remember { AdminClaudeUsageViewModel(getUsage, getEvents) }
    val state by viewModel.state.collectAsState()

    when {
        state.isLoading -> Div(attrs = { style { padding(40.px); property("display", "flex"); property("align-items", "center") } }) {
            WebSpinner()
            Div(attrs = { style { padding(0.px, 0.px, 0.px, 12.px); property("color", WebTextSecondary); fontSize(13.5.px) } }) { Text(S.loading) }
        }
        state.error != null -> Div(
            attrs = { style { padding(16.px); property("background", WebErrorBg); property("color", WebErrorInk); property("border-radius", "10px") } },
        ) { Text(if (state.error == "offline") S.errorOffline else S.errorGeneric) }
        else -> AdminClaudeUsageBody(state = state, onIntent = viewModel::onIntent)
    }
}

@Composable
private fun AdminClaudeUsageBody(state: AdminClaudeUsageState, onIntent: (AdminClaudeUsageIntent) -> Unit) {
    val usage = state.usage ?: return

    Div(attrs = { style { property("display", "flex"); property("align-items", "flex-end"); property("justify-content", "space-between"); property("gap", "20px"); property("flex-wrap", "wrap"); property("margin-bottom", "18px") } }) {
        Div {
            Div(attrs = { style { fontSize(17.px); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "3px") } }) { Text(S.title) }
            Div(attrs = { style { fontSize(12.px); property("color", WebTextSecondary); property("max-width", "52ch") } }) { Text(S.subtitle) }
        }
        Div(attrs = { style { property("display", "flex"); property("gap", "10px"); property("flex-wrap", "wrap") } }) {
            PeriodSegmentedControl(state.period) { onIntent(AdminClaudeUsageIntent.ChangePeriod(it)) }
            ViewToggle(state.view) { onIntent(AdminClaudeUsageIntent.ChangeView(it)) }
        }
    }

    Div(attrs = {
        style {
            property("display", "flex"); property("align-items", "center"); property("gap", "10px")
            property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-left", "3px solid $WebPrimaryMid")
            property("border-radius", "10px"); padding(10.px, 14.px); property("margin-bottom", "18px")
            fontSize(12.px); property("color", WebTextSecondary)
        }
    }) { Text(S.retentionBanner) }

    if (usage.summary.totalCalls == 0) {
        Div(attrs = { style { padding(36.px, 16.px); property("text-align", "center"); property("color", WebTextSecondary); fontSize(13.px) } }) {
            Text(S.emptyState)
        }
        return
    }

    ClaudeUsageKpis(usage)
    BreakdownLegend()

    when (state.view) {
        AdminClaudeUsageViewMode.ByAccount -> AccountsTable(usage.accounts, state, onIntent)
        AdminClaudeUsageViewMode.ByOrganisation -> OrganisationsList(usage.organisations, state, onIntent)
    }
}

@Composable
private fun PeriodSegmentedControl(current: AdminClaudeUsagePeriod, onChange: (AdminClaudeUsagePeriod) -> Unit) {
    val options = listOf(
        AdminClaudeUsagePeriod.Today to S.periodToday,
        AdminClaudeUsagePeriod.Last7Days to S.period7d,
        AdminClaudeUsagePeriod.Last30Days to S.period30d,
        AdminClaudeUsagePeriod.All to S.periodAll,
    )
    Div(attrs = { style { property("display", "inline-flex"); property("background", WebSurfaceDim); property("border", "1px solid $WebBorder"); property("border-radius", "9px"); padding(3.px); property("gap", "2px") } }) {
        options.forEach { (period, label) ->
            val active = period == current
            Div(
                attrs = {
                    onClick { onChange(period) }
                    style {
                        fontSize(12.5.px); property("font-weight", "600"); padding(6.px, 12.px); property("border-radius", "7px")
                        property("cursor", "pointer")
                        property("background", if (active) WebSurface else "transparent")
                        property("color", if (active) WebPrimary else WebTextSecondary)
                        if (active) property("box-shadow", "0 1px 2px rgba(30,58,74,0.06)")
                    }
                },
            ) { Text(label) }
        }
    }
}

@Composable
private fun ViewToggle(current: AdminClaudeUsageViewMode, onChange: (AdminClaudeUsageViewMode) -> Unit) {
    val options = listOf(
        AdminClaudeUsageViewMode.ByAccount to S.viewAccount,
        AdminClaudeUsageViewMode.ByOrganisation to S.viewOrganisation,
    )
    Div(attrs = { style { property("display", "inline-flex"); property("border", "1px solid $WebBorder"); property("border-radius", "9px"); property("overflow", "hidden") } }) {
        options.forEachIndexed { index, (mode, label) ->
            val active = mode == current
            Div(
                attrs = {
                    onClick { onChange(mode) }
                    style {
                        fontSize(12.5.px); property("font-weight", "600"); padding(7.px, 14.px); property("cursor", "pointer")
                        if (index > 0) property("border-left", "1px solid $WebBorder")
                        property("background", if (active) WebPrimary else WebSurface)
                        property("color", if (active) WebSurface else WebTextSecondary)
                    }
                },
            ) { Text(label) }
        }
    }
}

@Composable
private fun ClaudeUsageKpis(usage: AdminClaudeUsage) {
    val summary = usage.summary
    Div(attrs = { style { property("display", "grid"); property("grid-template-columns", "repeat(5, 1fr)"); property("gap", "12px"); property("margin-bottom", "18px") } }) {
        Kpi(S.kpiTokens, formatTokenCount(summary.totalInputTokens + summary.totalOutputTokens), "dont ${formatTokenCount(summary.totalInputTokens)} in · ${formatTokenCount(summary.totalOutputTokens)} out")
        Kpi(S.kpiCalls, summary.totalCalls.toString(), "")
        Kpi(S.kpiFailureRate, formatPercent(summary.failureRatePercent), "")
        Kpi(S.kpiAccounts, summary.activeAccountCount.toString(), "sur ${summary.activeOrganisationCount} organisations")
        Kpi(S.kpiCost, eurLabel(summary.totalEstimatedCostEur), S.kpiCostSubtitle)
    }
}

@Composable
private fun BreakdownLegend() {
    Div(attrs = { style { property("display", "flex"); property("gap", "14px"); property("flex-wrap", "wrap"); property("margin-bottom", "10px") } }) {
        BreakdownOrder.forEachIndexed { index, key ->
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "5px"); fontSize(11.px); property("color", WebTextSecondary) } }) {
                Div(attrs = { style { width(8.px); height(8.px); property("border-radius", "2px"); property("background", BreakdownColors[index]) } })
                Text(sourceEndpointLabel(key))
            }
        }
    }
}

@Composable
private fun AccountsTable(
    accounts: List<AdminClaudeUsageAccount>,
    state: AdminClaudeUsageState,
    onIntent: (AdminClaudeUsageIntent) -> Unit,
) {
    val averageShare = if (accounts.isEmpty()) 0.0 else 100.0 / accounts.size

    Div(attrs = {
        style {
            property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "14px")
            property("box-shadow", "0 1px 2px rgba(30,58,74,0.06)"); property("overflow", "hidden")
        }
    }) {
        ClaudeUsageGridRow(padding = "10px 16px") {
            ClaudeUsageHeaderCell(S.columnUser)
            ClaudeUsageHeaderCell(S.columnTokensIn, alignRight = true)
            ClaudeUsageHeaderCell(S.columnTokensOut, alignRight = true)
            ClaudeUsageHeaderCell(S.columnPercent)
            ClaudeUsageHeaderCell(S.columnBreakdown)
            ClaudeUsageHeaderCell(S.columnFailure)
        }

        accounts.forEachIndexed { index, account ->
            val isHot = account.percentOfTotal > averageShare * 2
            AccountRow(
                account = account,
                isHot = isHot,
                isLast = index == accounts.lastIndex,
                isExpanded = state.expandedAccountId == account.accountId,
                isLoadingEvents = state.eventsLoadingAccountId == account.accountId,
                events = state.eventsByAccount[account.accountId]?.events.orEmpty(),
                onToggle = { onIntent(AdminClaudeUsageIntent.ToggleAccountRow(account.accountId)) },
            )
        }
    }
}

@Composable
private fun OrganisationsList(
    organisations: List<AdminClaudeUsageOrganisation>,
    state: AdminClaudeUsageState,
    onIntent: (AdminClaudeUsageIntent) -> Unit,
) {
    val averageShare = if (organisations.isEmpty()) 0.0 else 100.0 / organisations.size

    organisations.forEach { org ->
        var open by remember(org.enterpriseId) { mutableStateOf(false) }
        val isHot = org.percentOfTotal > averageShare * 2

        Div(attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "14px")
                property("box-shadow", "0 1px 2px rgba(30,58,74,0.06)"); property("margin-bottom", "12px"); property("overflow", "hidden")
            }
        }) {
            Div(attrs = { onClick { open = !open }; style { property("cursor", "pointer"); property("border-left", if (isHot) "3px solid $WebWarning" else "3px solid transparent") } }) {
                ClaudeUsageGridRow(padding = "13px 16px") {
                    Div(attrs = { style { fontSize(13.5.px); property("font-weight", "700"); property("color", WebTextPrimary); property("display", "flex"); property("align-items", "center"); property("gap", "8px") } }) {
                        Text(org.enterpriseName)
                        if (isHot) Text(" 🔥")
                        Div(attrs = { style { fontSize(10.px); property("font-weight", "700"); property("color", WebTextSecondary); property("background", WebSurfaceDim); property("border-radius", "999px"); padding(2.px, 8.px) } }) { Text("${org.members.size} comptes") }
                        Div(attrs = { style { fontSize(10.px); property("font-weight", "700"); property("color", WebTextSecondary) } }) { Text(eurLabel(org.estimatedCostEur)) }
                    }
                    TokensCell(org.inputTokens, alignRight = true)
                    TokensCell(org.outputTokens, alignRight = true)
                    PercentCell(org.percentOfTotal)
                    BreakdownBar(org.tokensBySourceEndpoint)
                    FailureChip(org.failureRatePercent)
                }
            }

            if (open) {
                Div(attrs = { style { property("border-top", "1px solid $WebBorder"); property("padding-left", "20px") } }) {
                    org.members.forEachIndexed { index, member ->
                        AccountRow(
                            account = member,
                            isHot = false,
                            isLast = index == org.members.lastIndex,
                            isExpanded = state.expandedAccountId == member.accountId,
                            isLoadingEvents = state.eventsLoadingAccountId == member.accountId,
                            events = state.eventsByAccount[member.accountId]?.events.orEmpty(),
                            onToggle = { onIntent(AdminClaudeUsageIntent.ToggleAccountRow(member.accountId)) },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun AccountRow(
    account: AdminClaudeUsageAccount,
    isHot: Boolean,
    isLast: Boolean,
    isExpanded: Boolean,
    isLoadingEvents: Boolean,
    events: List<AdminClaudeUsageEvent>,
    onToggle: () -> Unit,
) {
    Div(attrs = { style { if (!isLast) property("border-bottom", "1px solid $WebBorder"); property("border-left", if (isHot) "3px solid $WebWarning" else "3px solid transparent") } }) {
        Div(attrs = { onClick { onToggle() }; style { property("cursor", "pointer") } }) {
            ClaudeUsageGridRow(padding = "11px 16px") {
                Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "6px") } }) {
                    Div {
                        Div(attrs = { style { fontSize(13.px); property("font-weight", "600"); property("color", WebTextPrimary) } }) {
                            Text("${account.firstName} ${account.lastName}".trim())
                            if (isHot) Text(" 🔥")
                        }
                        val enterpriseName = account.enterpriseName
                        if (enterpriseName != null) {
                            Div(attrs = { style { fontSize(11.px); property("color", WebTextSecondary) } }) { Text(enterpriseName) }
                        }
                    }
                }
                TokensCell(account.inputTokens, alignRight = true)
                TokensCell(account.outputTokens, alignRight = true)
                PercentCell(account.percentOfTotal)
                BreakdownBar(account.tokensBySourceEndpoint)
                FailureChip(account.failureRatePercent)
            }
        }

        if (isExpanded) {
            Div(attrs = { style { property("background", WebSurfaceDim); padding(10.px, 16.px, 16.px, 16.px) } }) {
                CostSummaryLine(account.estimatedCostEur, account.averageEstimatedCostPerCallEur)
                when {
                    isLoadingEvents -> Div(attrs = { style { property("display", "flex"); property("align-items", "center"); padding(8.px) } }) { WebSpinner(size = 18) }
                    events.isEmpty() -> Div(attrs = { style { fontSize(12.px); property("color", WebTextSecondary); padding(8.px) } }) { Text(S.emptyState) }
                    else -> EventsDetailTable(events)
                }
            }
        }
    }
}

private const val EventsDetailGridTemplate = "0.8fr 1fr 0.55fr 0.55fr 0.65fr 0.7fr 0.7fr 0.65fr"

@Composable
private fun CostSummaryLine(totalCost: Double, averageCostPerCall: Double) {
    Div(attrs = { style { property("display", "flex"); property("align-items", "baseline"); property("gap", "10px"); property("flex-wrap", "wrap"); padding(2.px, 2.px, 10.px, 2.px) } }) {
        Div(attrs = { style { fontSize(12.5.px); property("font-weight", "700"); property("color", WebTextPrimary) } }) { Text("💰 ${S.detailCostTotal} : ${eurLabel(totalCost)}") }
        Div(attrs = { style { fontSize(11.5.px); property("color", WebTextSecondary) } }) { Text("· ${eurLabel(averageCostPerCall)} ${S.detailCostAverage}") }
        Div(attrs = { style { fontSize(10.5.px); property("color", WebTextSecondary); property("font-style", "italic") } }) { Text("(${S.detailCostNote})") }
    }
}

@Composable
private fun EventsDetailTable(events: List<AdminClaudeUsageEvent>) {
    Div(attrs = { style { property("overflow-x", "auto") } }) {
        Div(attrs = { style { property("display", "grid"); property("grid-template-columns", EventsDetailGridTemplate); property("gap", "8px"); padding(6.px, 10.px); fontSize(10.px); property("font-weight", "700"); property("color", WebTextSecondary); property("text-transform", "uppercase"); property("letter-spacing", "0.04em") } }) {
            Text(S.detailDate); Text(S.detailAction); Text(S.detailTokensIn); Text(S.detailTokensOut); Text(S.detailSource); Text(S.detailOutput); Text(S.detailResult); Text(S.detailCost)
        }
        events.forEach { event -> EventDetailRow(event) }
    }
}

@Composable
private fun EventDetailRow(event: AdminClaudeUsageEvent) {
    var showOutput by remember(event.id) { mutableStateOf(false) }

    Div(attrs = { style { property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "8px"); property("margin-bottom", "6px") } }) {
        Div(attrs = { style { property("display", "grid"); property("grid-template-columns", EventsDetailGridTemplate); property("gap", "8px"); property("align-items", "center"); padding(8.px, 10.px) } }) {
            Div(attrs = { style { fontSize(11.5.px); property("color", WebTextSecondary) } }) { Text(formatDateTimeShort(event.createdAt)) }
            Div(attrs = { style { fontSize(11.5.px); property("color", WebTextPrimary) } }) { Text(sourceEndpointLabel(event.sourceEndpoint)) }
            Div(attrs = { style { fontSize(11.5.px); property("font-family", "ui-monospace, monospace"); property("color", WebTextSecondary); property("text-align", "right") } }) { Text(event.inputTokens.toString()) }
            Div(attrs = { style { fontSize(11.5.px); property("font-family", "ui-monospace, monospace"); property("color", WebTextSecondary); property("text-align", "right") } }) { Text(event.outputTokens.toString()) }
            SourceFileChip(event)
            OutputChip(expanded = showOutput, hasText = !event.responseText.isNullOrBlank()) { showOutput = !showOutput }
            OutcomeBadge(event.outcome)
            Div(attrs = { style { fontSize(11.px); property("font-family", "ui-monospace, monospace"); property("color", WebTextSecondary); property("text-align", "right") } }) { Text(eurLabel(event.estimatedCostEur)) }
        }

        if (showOutput) {
            val responseText = event.responseText
            Div(attrs = { style { property("border-top", "1px dashed $WebBorder"); padding(10.px) } }) {
                if (responseText.isNullOrBlank()) {
                    Div(attrs = { style { fontSize(11.5.px); property("color", WebTextSecondary) } }) { Text("(aucune réponse — appel jamais arrivé jusqu'à Claude)") }
                } else {
                    Pre(attrs = { style { fontSize(11.px); property("font-family", "ui-monospace, monospace"); property("background", WebSurfaceDim); property("border-radius", "8px"); padding(10.px); property("white-space", "pre-wrap"); property("word-break", "break-word"); property("margin", "0") } }) {
                        Text(responseText)
                    }
                }
            }
        }
    }
}

@Composable
private fun SourceFileChip(event: AdminClaudeUsageEvent) {
    when {
        !event.hasSourceFile -> Div(attrs = { style { fontSize(11.px); property("color", WebTextSecondary) } }) { Text("—") }
        event.sourceFileExpired -> FileBadge(S.fileExpired, bg = WebSurfaceDim, ink = WebTextSecondary)
        event.sourceFilePresignedUrl != null -> A(
            href = event.sourceFilePresignedUrl,
            attrs = { attr("target", "_blank"); attr("rel", "noopener noreferrer") },
        ) { FileBadge(S.fileAvailable, bg = WebAccentLight, ink = WebPrimary) }
        else -> FileBadge(S.fileAvailable, bg = WebSurfaceDim, ink = WebTextSecondary)
    }
}

@Composable
private fun OutputChip(expanded: Boolean, hasText: Boolean, onClick: () -> Unit) {
    val label = if (hasText) S.outputAvailable else S.outputUnavailable
    Div(
        attrs = {
            onClick { onClick() }
            style {
                fontSize(11.px); property("font-weight", "700"); padding(3.px, 9.px); property("border-radius", "999px")
                property("display", "inline-block"); property("cursor", "pointer"); property("width", "fit-content")
                property("background", if (expanded) WebPrimary else WebPrimaryLight)
                property("color", if (expanded) WebSurface else WebPrimary)
            }
        },
    ) { Text(label) }
}

@Composable
private fun FileBadge(label: String, bg: String, ink: String) {
    Div(attrs = { style { fontSize(11.px); property("font-weight", "700"); padding(3.px, 9.px); property("border-radius", "999px"); property("background", bg); property("color", ink); property("display", "inline-block") } }) { Text(label) }
}

@Composable
private fun OutcomeBadge(outcome: String) {
    val (bg, ink) = when (outcome) {
        "Success" -> WebSuccessBg to WebSuccessInk
        "Failed" -> WebErrorBg to WebErrorInk
        else -> WebWarningBg to WebWarning
    }
    Div(attrs = { style { fontSize(11.px); property("font-weight", "700"); padding(3.px, 9.px); property("border-radius", "999px"); property("background", bg); property("color", ink); property("display", "inline-block"); property("width", "fit-content") } }) {
        Text(outcomeLabel(outcome))
    }
}

@Composable
private fun TokensCell(value: Long, alignRight: Boolean = false) {
    Div(attrs = { style { fontSize(13.px); property("font-family", "ui-monospace, monospace"); property("color", WebTextPrimary); if (alignRight) property("text-align", "right") } }) {
        Text(formatTokenCount(value))
    }
}

@Composable
private fun PercentCell(percent: Double) {
    Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "8px") } }) {
        Div(attrs = { style { property("flex", "1"); height(6.px); property("border-radius", "999px"); property("background", WebSurfaceDim); property("overflow", "hidden") } }) {
            Div(attrs = { style { property("display", "block"); height(100.percent); property("border-radius", "999px"); property("background", WebPrimaryMid); width(percent.coerceIn(0.0, 100.0).percent) } })
        }
        Div(attrs = { style { fontSize(12.px); property("font-family", "ui-monospace, monospace"); property("color", WebTextSecondary); property("font-weight", "600"); property("width", "42px"); property("text-align", "right") } }) {
            Text(formatPercent(percent))
        }
    }
}

@Composable
private fun BreakdownBar(bySourceEndpoint: Map<String, Long>) {
    val total = bySourceEndpoint.values.sum().coerceAtLeast(1)
    Div(attrs = { style { property("display", "flex"); height(8.px); property("border-radius", "999px"); property("overflow", "hidden"); property("background", WebSurfaceDim) } }) {
        BreakdownOrder.forEachIndexed { index, key ->
            val value = bySourceEndpoint[key] ?: 0L
            if (value > 0) {
                val widthPercent = value * 100.0 / total
                Div(attrs = { style { width(widthPercent.percent); height(100.percent); property("background", BreakdownColors[index]) } })
            }
        }
    }
}

@Composable
private fun FailureChip(percent: Double) {
    val (bg, ink) = when {
        percent < 5.0 -> WebSuccessBg to WebSuccessInk
        percent < 15.0 -> WebWarningBg to WebWarning
        else -> WebErrorBg to WebErrorInk
    }
    Div(attrs = { style { fontSize(11.5.px); property("font-weight", "700"); padding(3.px, 8.px); property("border-radius", "999px"); property("background", bg); property("color", ink); property("display", "inline-block"); property("min-width", "42px"); property("text-align", "center") } }) {
        Text(formatPercent(percent))
    }
}

@Composable
private fun ClaudeUsageGridRow(padding: String, content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            property("display", "grid"); property("grid-template-columns", ClaudeUsageGridTemplate)
            property("align-items", "center"); property("gap", "8px"); property("padding", padding)
        }
    }) { content() }
}

@Composable
private fun ClaudeUsageHeaderCell(label: String, alignRight: Boolean = false) {
    Div(attrs = { style { fontSize(10.5.px); property("font-weight", "700"); property("color", WebTextSecondary); property("text-transform", "uppercase"); property("letter-spacing", "0.05em"); if (alignRight) property("text-align", "right") } }) {
        Text(label)
    }
}

/** No String.format (JVM-only, forbidden in commonMain/jsMain) — plain integer division/modulo for
 * thousand separators, since token counts here are always non-negative. */
private fun formatTokenCount(value: Long): String {
    val text = value.toString()
    val sb = StringBuilder()
    for ((index, char) in text.reversed().withIndex()) {
        if (index > 0 && index % 3 == 0) sb.append(' ')
        sb.append(char)
    }
    return sb.reverse().toString()
}

private fun formatPercent(value: Double): String {
    val rounded = kotlin.math.round(value * 10) / 10.0
    return "$rounded%"
}

/** Indicative-cost display — "~" prefix everywhere it's shown reinforces it's an estimate, not a
 * real billed amount (see ClaudeCostEstimator's remarks, backend repo). Rounds to 4 decimals since
 * per-call costs here are routinely fractions of a cent. No String.format (JVM-only, forbidden in
 * commonMain/jsMain). MIL-98 — amounts are in euros (backend `…Eur`, from the stored CostEur). */
private fun formatEur(amount: Double, separator: String): String {
    // At least 2 decimals, up to 4 for a fraction of a cent: "~1,01 €", "~0,0123 €".
    val text = AiCreditFormat.decimal(amount, 4, separator).trimEnd('0').let { if (it.substringAfter(separator, "").length < 2) AiCreditFormat.decimal(amount, 2, separator) else it }
    return "~$text €"
}

/** MIL-98 — euro amount with the decimal mark of the current language, as the app's other amounts. */
@Composable
private fun eurLabel(amount: Double): String = formatEur(amount, LocalStrings.current.aiCredit.decimalSeparator)

/** "2026-10-05T14:22:00+00:00" -> "05/10 14:22". */
private fun formatDateTimeShort(iso: String): String {
    val datePart = iso.substringBefore('T')
    val timePart = iso.substringAfter('T', missingDelimiterValue = "")
    val dateParts = datePart.split("-")
    val ddMm = if (dateParts.size == 3) "${dateParts[2]}/${dateParts[1]}" else datePart
    val hhmm = if (timePart.length >= 5) timePart.take(5) else null
    return if (hhmm != null) "$ddMm $hhmm" else ddMm
}
